import { MapFocusObject } from '../../core/map-object-focus';
import { CacheService } from '../../../services/cache.service';
import { MapAreaBoundaryColor, MapFeatureColors } from '../../core/map-object-colors';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as L from 'leaflet';
import { Router } from '@angular/router';
import { LabelAnchor, labelAnchor } from './ring-geometry';
import {
  AreaFeatureBase,
  AreaMapObject,
} from '../../core/base/area-feature.base';
import { CountChip, countChipIcon, countChipSize } from '../../core/count-badge';
import {
  TerritoryFallbackColor,
  territoryDisplayColors,
  territoryLabelTextColor,
} from './territory-colors';
import { MapNavigationService } from '../../core/map-navigation.service';
import { EditableFeature } from '../../core/editable-feature';
import { PopoverFeature } from '../../core/popover-feature';
import { MapInteractionController } from '../../components/map/map-interaction-controller';
import { AuthService } from '../../../services/auth.service';
import { TerritoryBoundaryEditor } from './territory-boundary-editor';
import { ringAreaSqMeters, ringsOf } from './boundary-snapping';
import { MapObjectPopover } from '../../core/map-object-popover';

export interface TerritoryMapObject extends AreaMapObject {
  name: string; status: string;
  assignedUserId: number | null; color: string | null;
}

export interface Territory extends TerritoryMapObject {
  geometryVersion: number;
  depotId: number | null;
  /** 'Manual' | 'AgentPlan' — who drew it. */
  source: string;
  approvedByUserId: number | null; approvedAt: string | null;
  createdAt: string; updatedAt: string;
}

export interface TerritoryWithMetrics {
  territory: Territory;
  /** The two numbers the chip shows. Counted the same way - each lead and each
   *  customer belongs to the smallest active patch that contains it, and to no
   *  other - so the pair can be read as one sentence about the patch.
   *
   *  `customers` is NULL for a viewer without Customer.View: this page is
   *  reached with a lead permission, and the server withholds the number
   *  rather than sending a nought that would read as an empty patch. The chip
   *  then shows the one number it was given. */
  leads: number; customers: number | null;
  openCases: number; won: number; lost: number;
  openPipelineValue: number; areaSqKm: number; density: number;
}

interface TerritoryGeometry {
  readonly source: string;
  readonly parsed: unknown;
  readonly bounds: L.LatLngBounds;
  readonly editable: boolean;
}

/** Said only when there is something to say. A viewer who may not see customer
 *  numbers is not told "0 customers", which would be a different claim. */
function customerPhrase(row: TerritoryWithMetrics): string {
  return row.customers == null ? '' : ` · ${row.customers} customers`;
}

/**
 * Territory boundaries as a layer on the persistent workspace map.
 *
 * Holds the lightweight map read model used to draw and pick a boundary. The
 * form, assignment, AI planning and errors stay in the routed control surface.
 * That page may add metrics by id, but the polygon layer does not depend on
 * those analytics and never fetches them itself.
 */
@Injectable({ providedIn: 'root' })
export class TerritoryFeature extends AreaFeatureBase<TerritoryMapObject>
implements EditableFeature<TerritoryMapObject>, PopoverFeature<TerritoryMapObject> {
  static readonly Key = 'territories';

  override readonly key = TerritoryFeature.Key;
  override readonly name = 'Territories';
  override readonly icon = 'pentagon';
  override readonly iconColor = MapFeatureColors.territories;
  override readonly page = '/leads/territories';
  override objects: TerritoryMapObject[] = [];

  /** Optional analytics keyed by the map object's stable id. The persistent
   * map never needs these to draw a boundary; the Territories workspace adds
   * them when it is open. */
  private metrics = new Map<number, TerritoryWithMetrics>();
  private workspaceDataSupplied = false;
  private popoversEnabled = false;
  private mapObjectsLoaded = false;
  get count(): number | null {
    return this.mapObjectsLoaded ? this.objects.length : null;
  }
  private mapObjectsLoad?: Promise<void>;

  /** The patch whose shape is being dragged right now. Its saved outline is not
   *  drawn while that lasts: two outlines for one patch reads as two patches,
   *  and the stale one is the one that looks authoritative. */
  /** Territories the editor is drawing; the map layer leaves them to it. */
  private hidden = new Set<number>();

  /** Rep names by user id, so a boundary can say who works it. The layer is
   *  given them rather than fetching: the page already has the roster. */
  private repNames: ReadonlyMap<number, string> = new Map();

  /** The map, for the two things a label cannot decide without it: whether it
   *  FITS the shape at this zoom, and whether it lands on another label. */
  private map?: L.Map;
  private editor?: TerritoryBoundaryEditor;
  editingAreaSqKm = 0;
  get editing(): boolean { return this.editor?.active ?? false; }
  get editVertexCount(): number { return this.editor?.vertexCount ?? 0; }
  get canUndoEdit(): boolean { return this.editor?.canUndo ?? false; }
  get shapeChanged(): boolean { return this.editor?.changed ?? false; }
  get changedShapes(): { id: number; rings: L.LatLng[][] }[] {
    return this.editor?.changedShapes ?? [];
  }

  canEdit(objectId: number): boolean {
    const object = this.metrics.get(objectId)?.territory;
    return !!this.interactions && this.enabled
      && this.workspaceDataSupplied
      && this.auth?.hasPermission('Territory.Manage') === true
      && !!object && this.geometryFor(object)?.editable === true;
  }

  beginEdit(objectId: number): void {
    if (!this.canEdit(objectId)) return;
    const object = this.metrics.get(objectId)!.territory;
    const rings = ringsOf(object.boundaryGeoJson);
    this.endEdit();
    this.editor = this.interactions!.createSession(map => new TerritoryBoundaryEditor(
      map,
      edited => { this.editingAreaSqKm = ringAreaSqMeters(edited[0]) / 1_000_000; },
      ids => this.hideWhileEditing(ids),
    ));
    this.interactions!.beginEdit();
    this.hideWhileEditing([objectId]);
    this.editor.start([
      { id: objectId, rings },
      ...this.objects.filter(item => item.id !== objectId)
        .map(item => ({ id: item.id, rings: ringsOf(item.boundaryGeoJson) }))
        .filter(shape => shape.rings.length > 0),
    ], this.displayColors().get(objectId) ?? TerritoryFallbackColor);
    this.editingAreaSqKm = ringAreaSqMeters(rings[0]) / 1_000_000;
  }

  endEdit(): void {
    if (!this.editing) return;
    this.editor?.stop();
    this.editor = undefined;
    this.interactions?.endEdit();
    this.interactions?.setContainerMode();
    this.hideWhileEditing([]);
    this.editingAreaSqKm = 0;
  }

  undoEdit(): void { this.editor?.undo(); }
  private readonly marks: PatchMarks[] = [];
  private get geometryByTerritoryId(): Map<number, TerritoryGeometry> {
    return this.cache.getOrCreate('territory-geometry', 'objects', () => new Map<number, TerritoryGeometry>());
  }
  private readonly onViewChange = () => this.scheduleLayout();
  /** The map can be taken down between a layout being queued and it running -
   *  a routed page leaving, a test tearing its container down. Everything a
   *  layout asks a map is about where things are on it, and a map that is
   *  gone does not answer those: it throws. So the layer lets go of it. */
  private readonly onUnload = () => { this.clearObjectFocus(); this.endEdit(); this.map = undefined; };
  private layoutQueued = false;

  /**
   * Lay out once, after this burst of changes.
   *
   * The map fills in around the labels: lead clusters arrive from their own
   * request well after the boundaries are drawn, and a name placed before they
   * landed can be sitting on one by the time anybody looks. Measured on dev
   * after a merge: one label on a cluster that had loaded a moment later.
   *
   * Coalesced because `layeradd` fires per layer - a hundred markers arriving
   * together is one layout, not a hundred. Safe to hear our own labels being
   * added: laying out only toggles a class and an offset, so it cannot start
   * the loop it would otherwise be inviting.
   */
  private scheduleLayout(): void {
    if (this.layoutQueued) return;
    this.layoutQueued = true;
    setTimeout(() => {
      this.layoutQueued = false;
      this.layoutLabels();
    });
  }

  constructor(
    private readonly navigation: MapNavigationService,
    private readonly cache: CacheService,
    private readonly http?: HttpClient,
    private readonly zone?: NgZone,
    private readonly interactions?: MapInteractionController,
    private readonly auth?: AuthService,
    private readonly router?: Router,
  ) {
    super();
    this.selection.changed$.subscribe(changed =>
      this.updateSelectedShapes(changed));
  }

  resetSession(): void {
    this.clearObjectFocus();
    this.selection.clear();
    this.endEdit();
    this.disablePopovers();
    this.objects = [];
    this.metrics.clear();
    this.geometryByTerritoryId.clear();
    this.hidden.clear();
    this.repNames = new Map();
    this.workspaceDataSupplied = false;
    this.mapObjectsLoaded = false;
    this.mapObjectsLoad = undefined;
    this.marks.length = 0;
    this.shapesByObjectId.clear();
    this.layer.clearLayers();
    this.notifyChanged();
  }

  async init(): Promise<void> {
    await this.loadMapObjectsIfEnabled();
  }

  setObjects(objects: TerritoryMapObject[]): void {
    // The initial map request can finish after the routed workspace request.
    // In that race, keep the workspace's newer draft-capable rows rather than
    // replacing them with the startup snapshot.
    this.mapObjectsLoaded = true;
    if (this.workspaceDataSupplied) return;
    this.objects = objects;
    this.render();
    this.notifyChanged();
  }

  /** The workspace supplies its richer rows without making the map endpoint
   * compute analytics. Draft geometry also replaces the corresponding map
   * objects immediately, so the editor and saved outline cannot diverge. */
  setRows(rows: TerritoryWithMetrics[]): void {
    const target = this.navigation.focusedObject;
    if (target?.featureKey === this.key && this.objects.some(object => object.id === target.objectId)
        && !rows.some(row => row.territory.id === target.objectId)) this.navigation.clearFocus();
    this.workspaceDataSupplied = true;
    this.mapObjectsLoaded = true;
    this.metrics = new Map(rows.map(row => [row.territory.id, row]));
    this.objects = rows.map(row => row.territory);
    this.render();
    this.notifyChanged();
  }

  metricsFor(id: number): TerritoryWithMetrics | undefined {
    return this.metrics.get(id);
  }

  /**
   * Stop drawing the saved outlines of the patches the editor is drawing.
   *
   * A SET, not one id. Moving a corner that two patches share moves both, and
   * the editor draws both - so hiding only the one under the cursor left the
   * neighbour on screen twice: its saved outline standing where the border
   * was, and the editor's copy following the drag. Two lines for one border,
   * and the stale one is the more solid-looking of the two.
   */
  hideWhileEditing(ids: readonly number[]): void {
    const next = new Set(ids);
    if (next.size === this.hidden.size && [...next].every(id => this.hidden.has(id))) return;
    this.hidden = next;
    this.render();
  }

  /**
   * The shared map projection used only for view-dependent label layout.
   *
   * Labels need it for the judgements that depend on the current view: a name
   * that fits a patch at street zoom crosses both its edges three zoom levels
   * out, and two labels that are far apart become one illegible pile. Both are
   * re-decided whenever the view settles.
   */
  useMap(map: L.Map, obstacles?: () => readonly Element[]): void {
    this.map?.off('moveend zoomend layeradd layerremove', this.onViewChange);
    this.map?.off('unload', this.onUnload);
    this.map = map;
    this.useSelectionMap(map);
    this.obstacles = obstacles;
    // The view settling, and the map's contents changing under the labels.
    map.on('moveend zoomend layeradd layerremove', this.onViewChange);
    map.on('unload', this.onUnload);
  }

  /** Chrome the SHELL floats over the map - the restore chip, the agent chip.
   *  The layer cannot know about those; the shell that draws them can. */
  private obstacles?: () => readonly Element[];

  /** Who works each patch, by user id. Names the page already holds. */
  setRepNames(names: ReadonlyMap<number, string>): void {
    this.repNames = names;
    this.render();
  }

  /** Frame a boundary on the shared map. Silent on an unparseable one — a bad
   *  boundary must not take the view with it. */
  zoomTo(t: TerritoryMapObject): void {
    if (this.geometryFor(t)) void this.navigation.locate(this.focusTarget(t));
  }

  override render(): void {
    this.layer.clearLayers();
    this.marks.length = 0;
    this.shapesByObjectId.clear();
    const liveIds = new Set(this.objects.map(object => object.id));
    for (const id of this.geometryByTerritoryId.keys()) {
      if (!liveIds.has(id)) this.geometryByTerritoryId.delete(id);
    }
    if (!this.enabled) return;
    if (!this.workspaceDataSupplied && !this.mapObjectsLoaded) {
      void this.loadMapObjectsIfEnabled();
      return;
    }
    this.draw();
  }

  private async loadMapObjectsIfEnabled(): Promise<void> {
    if (!this.enabled || this.workspaceDataSupplied || this.mapObjectsLoaded
        || !this.http) return;
    if (this.mapObjectsLoad) return this.mapObjectsLoad;

    const version = this.cache.sessionVersion;
    const load = firstValueFrom(
      this.cache.load('territories', 'map', () =>
        this.http!.get<TerritoryMapObject[]>('/api/territories/map')),
      { defaultValue: null },
    ).then(objects => {
      if (objects && version === this.cache.sessionVersion) this.setObjects(objects);
    }).catch(error => {
      // A failed business layer must not prevent the base map and independently
      // loaded features from becoming usable. A later enable retries the read.
      console.error('Failed to load Territory map data', error);
    }).finally(() => {
      if (this.mapObjectsLoad === load) this.mapObjectsLoad = undefined;
    });
    this.mapObjectsLoad = load;
    return load;
  }

  private draw(): void {
    const colors = this.displayColors();
    const selectedShapes: L.GeoJSON[] = [];
    for (const t of this.objects) {
      const metric = this.metrics.get(t.id);
      // Being edited means the EDITOR is drawing this patch's outline, so this
      // layer does not - two lines for one border, and the stale one looks the
      // more solid of the two. It does not mean the patch has left the map:
      // its name and its count are not the outline and are not the editor's to
      // draw. They used to go with it, so clicking a patch made the number on
      // it vanish and left a bare shape - selecting one starts editing it, so
      // that was every click.
      const outlineIsTheEditors = this.hidden.has(t.id);
      const isSelected = this.selection.has(t.id);
      const color = colors.get(t.id) ?? TerritoryFallbackColor;
      const geometry = this.geometryFor(t);
      if (!geometry) continue;
      const shape = L.geoJSON(geometry.parsed as never, {
        style: {
          color: MapAreaBoundaryColor,
          opacity: 1,
          fillColor: color,
          weight: 2,
          fillOpacity: 0.15,
          // A retired territory is history: visible, not competing.
          dashArray: t.status === 'Retired' ? '4,6' : undefined,
          className: `tr-pickable${isSelected ? ' map-area-selected' : ''}`,
        },
      });
      // The marks are hung off the shape's geometry, which exists whether or
      // not the shape itself goes on the map.
      this.addMarks(t, metric, shape, color);
      if (outlineIsTheEditors) continue;

      shape.on('click', e => {
        // Stopped, or the map hears the same click as "you clicked nothing" and
        // deselects the patch that was just selected.
        L.DomEvent.stop(e);
        const hit = e as L.LeafletMouseEvent;
        this.pick(t, hit.latlng, hit.originalEvent);
      });
      this.layer.addLayer(shape);
      this.shapesByObjectId.set(t.id, shape);
      if (isSelected) selectedShapes.push(shape);
    }

    // SVG paths in Leaflet are stacked by DOM order, not by a useful CSS
    // z-index. Raise the selected originals after every boundary exists so a
    // later unselected neighbour cannot paint over their shared edge.
    for (const shape of selectedShapes) shape.bringToFront();

    // After every label exists: fitting and collisions are decided between
    // them, not one at a time.
    this.scheduleLayout();
  }

  /**
   * The colour each patch is DRAWN in.
   *
   * Its own, unless a neighbour already holds one too close to tell it from -
   * `territory-colors` explains why the saved one cannot be trusted on its
   * own. Decided over every patch, including one the editor is drawing: it is
   * still on the map, still carrying its count in its own colour, and still
   * the thing its neighbours have to be told apart from. Leaving it out
   * repainted whichever neighbours had been placed around it, so selecting a
   * patch recoloured the map next to it.
   *
   * Geometry and bounds are cached by object id + GeoJSON text, so metrics,
   * assignee names and selection changes never parse the same boundary again.
   */
  private displayColors(): Map<number, string> {
    return territoryDisplayColors(this.objects
      .map(territory => ({
        id: territory.id,
        color: territory.color,
        bounds: this.geometryFor(territory)?.bounds ?? null,
      })));
  }

  private geometryFor(
    territory: TerritoryMapObject,
  ): TerritoryGeometry | null {
    const cached = this.geometryByTerritoryId.get(territory.id);
    if (cached?.source === territory.boundaryGeoJson) return cached;
    try {
      const parsed: unknown = JSON.parse(territory.boundaryGeoJson);
      const bounds = L.geoJSON(parsed as never).getBounds();
      const geometry = {
        source: territory.boundaryGeoJson, parsed, bounds,
        editable: ringsOf(territory.boundaryGeoJson).length > 0,
      };
      this.geometryByTerritoryId.set(territory.id, geometry);
      return geometry;
    } catch {
      this.geometryByTerritoryId.delete(territory.id);
      return null;
    }
  }

  /** The count chip and the name, both hung off the roomiest point inside the
   *  shape, and both skipped when there is no such point to hang them on. */
  private addMarks(
    territory: TerritoryMapObject,
    metric: TerritoryWithMetrics | undefined,
    shape: L.GeoJSON,
    color: string,
  ): void {
    const rings = ringsOfLayer(shape);
    if (!rings.length) return;
    const anchor = labelAnchor(rings);
    if (!anchor) return;

    const count = metric ? this.addCount(metric, anchor, color) : undefined;
    const label = this.addLabel(territory, anchor, color);
    const size = metric ? countChipSize(chipOf(metric, color)) : { width: 0, height: 0 };
    this.marks.push({
      label, count, anchor,
      // Half of each side, not one radius. The circle this replaced was as
      // wide as it was tall, so one number answered both of the questions the
      // layout asks - does it fit, and how far off it must a name sit - and a
      // chip that is three times wider than it is tall answers them apart.
      radius: size.width / 2,
      halfHeight: size.height / 2,
    });
  }

  /**
   * What the patch holds - leads and customers - in one chip.
   *
   * BOTH numbers, because a patch is worked on both: the leads are what there
   * is to win in it and the customers are what is already being served in it,
   * and a rep deciding where to spend a day is comparing exactly those two.
   * Showing one and hiding the other made the map answer half a question.
   *
   * A CHIP, NOT THE CLUSTER CIRCLE. The circle the clusters use means "these
   * markers are folded up under me, zoom in and I come apart"; a territory
   * count never comes apart, and borrowing the circle made a patch's leads
   * read as a pile of pins sitting at its centre. Two counts also need two
   * cells, which a circle does not have. The distinction is written up in
   * map/core/count-badge.ts.
   *
   * It is edged in the patch's own colour rather than one colour for all of
   * them, which is the rule the other features follow. On this page it also
   * answers a question a shared colour could not: which chip belongs to which
   * patch, when two of them sit either side of a border. Inside, each number
   * keeps the colour its own feature has everywhere else on this map, so no
   * legend has to explain which is which.
   *
   * How many of those leads are still open is the next question, and it is on
   * this chip's hover rather than competing with the first two. The polygon
   * does not repeat the same hover: its permanent name is already visible,
   * and opening Leaflet's tooltip over that name draws two labels on top of
   * each other.
   *
   * Zero is drawn, not omitted. A patch with nothing in it is worth reading,
   * and a missing chip cannot be told from metrics that have not landed.
   *
   * CLICKABLE, and it picks the patch it belongs to - the same thing clicking
   * the patch does, which is what makes it safe. It started out deaf, on the
   * grounds that a badge taking the click would stop the boundary underneath
   * from being selectable; the objection disappears once the badge answers
   * with the same patch. What it buys is that the number is a target. It is
   * the thing on the map somebody is reading, it is what they aim at to see
   * the leads behind it, and relying on the click falling through to the fill
   * meant aiming at a number and hitting whatever happened to be under it.
   */
  private addCount(
    row: TerritoryWithMetrics,
    anchor: LabelAnchor,
    color: string,
  ): L.Marker {
    const marker = L.marker(anchor.at, {
      icon: countChipIcon(chipOf(row, color)),
      keyboard: false,
    });
    marker.bindTooltip(
      `${row.territory.name} · ${row.leads} leads`
      + `${customerPhrase(row)} · ${row.openCases} to work`);
    marker.on('click', e => {
      // Stopped for the reason the boundary stops it: unstopped, the map hears
      // the same click as "you clicked nothing" and drops the patch that was
      // just picked.
      L.DomEvent.stop(e);
      const hit = e as L.LeafletMouseEvent;
      this.pick(row.territory, hit.latlng, hit.originalEvent);
      this.openTerritoriesPage();
    });
    this.layer.addLayer(marker);
    return marker;
  }

  enablePopovers(): void { this.popoversEnabled = true; }

  disablePopovers(): void { this.popoversEnabled = false; }

  private clearObjectFocus(): void {
    if (this.navigation.hasObjectFocus(this.key)) this.navigation.clearFocus();
  }

  inspectObject(id: string | number): void {
    const object = this.objects.find(item => item.id === id);
    if (object) this.focusObject(object);
  }

  focusObject(object: TerritoryMapObject, anchor?: L.LatLng): void {
    void this.navigation.focus(this.focusTarget(object, anchor));
  }

  private focusTarget(object: TerritoryMapObject, anchor?: L.LatLng): MapFocusObject {
    return {
      reference: { featureKey: this.key, objectId: object.id },
      geometry: JSON.parse(object.boundaryGeoJson),
      popup: this.objectPopover(object), anchor,
    };
  }

  /** Inspection uses the business object; selection uses the same business identity. */
  private pick(
    territory: TerritoryMapObject,
    at: L.LatLng,
    input?: MouseEvent,
  ): void {
    this.insideAngular(() => {
      if (!this.enabled) return;
      if (this.selectionEnabled) {
        if (!this.selectObject(territory.id, input) || !this.selection.has(territory.id)) return;
      }
      if (this.popoversEnabled && this.map) this.focusObject(territory, at);
    });
  }

  private insideAngular<T>(action: () => T): T {
    return this.zone ? this.zone.run(action) : action();
  }

  private objectPopover(territory: TerritoryMapObject): MapObjectPopover {
    const row = this.metrics.get(territory.id);
    const assignedTo = territory.assignedUserId == null
      ? 'Unassigned'
      : this.repNames.get(territory.assignedUserId)
        ?? `User ${territory.assignedUserId}`;
    return {
      type: 'Territory',
      title: territory.name,
      subtitle: territory.status,
      values: [
        { label: 'Assigned to', value: assignedTo },
        { label: 'Leads', value: row?.leads },
        { label: 'Customers', value: row?.customers },
        { label: 'Open cases', value: row?.openCases },
        { label: 'Won', value: row?.won },
        { label: 'Lost', value: row?.lost },
        {
          label: 'Open pipeline',
          value: row
            ? row.openPipelineValue.toLocaleString(undefined, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            })
            : undefined,
        },
        {
          label: 'Area',
          value: row ? `${row.areaSqKm.toLocaleString()} km²` : undefined,
        },
      ],
    };
  }

  /**
   * A territory's name and who works it - on the patch.
   *
   * Placed at the roomiest point inside the shape rather than at its centre:
   * a patch bent around a lake has a centre that is not in it, and a label
   * standing outside its own boundary names the wrong thing.
   *
   * Added here, judged in `layoutLabels` — whether it fits, whether it
   * collides, and how far it has to stand off its own count chip can only
   * be answered against the current view, and the view changes without the
   * objects changing.
   */
  /**
   * Where a patch's own labels lead: the page that manages territories.
   *
   * The name and the count are the two things on this map that are ABOUT a
   * territory rather than part of the picture, so they are what somebody aims
   * at when they want to do something to one. Until now they answered by
   * selecting it and nothing else, which on any page but Territories left the
   * reader with a highlighted outline and no way forward.
   *
   * Selecting still happens - it is what opens the patch once the page is
   * there - and navigating to the page you are already on is a no-op, so the
   * behaviour on Territories itself is unchanged.
   */
  private openTerritoriesPage(): void {
    // Through the zone when there is one: this runs from a Leaflet DOM
    // handler, which Angular is not watching, and a navigation started
    // outside the zone leaves the new page unrendered until something else
    // wakes it.
    const go = () => void this.router?.navigateByUrl('/leads/territories');
    if (this.zone) this.zone.run(go);
    else go();
  }

  private addLabel(
    t: TerritoryMapObject,
    anchor: LabelAnchor,
    color: string,
  ): L.Tooltip {
    const rep = t.assignedUserId != null ? this.repNames.get(t.assignedUserId) : null;
    const label = L.tooltip({
      permanent: true,
      direction: 'center',
      className: 'tr-label',
      // On, and it does swallow clicks on the boundary underneath - which is
      // only safe because it now answers with the same patch the boundary
      // would have. That is the argument the count chip already made: a label
      // that takes the click and does something ELSE is the version of this
      // that breaks selection.
      interactive: true,
      opacity: 1,
    })
      .setLatLng(anchor.at)
      .setContent(
        `<span class="tr-label-name">${escapeHtml(t.name)}</span>`
        + (rep ? `<span class="tr-label-rep">${escapeHtml(rep)}</span>` : ''));

    // Tie the label to the boundary it names. Using a DOM custom property
    // keeps the colour out of the HTML string while still letting CSS draw a
    // compact map-layer badge instead of another piece of basemap text.
    label.on('add', () => {
      const style = label.getElement()?.style;
      style?.setProperty('--tr-label-color', color);
      style?.setProperty('--tr-label-text-color', territoryLabelTextColor(color));
    });
    label.on('click', e => {
      // Stopped for the reason the chip stops it: unstopped, the map hears the
      // same click as "you clicked nothing" and drops the patch just picked.
      L.DomEvent.stop(e);
      const hit = e as L.LeafletMouseEvent;
      this.pick(t, hit.latlng, hit.originalEvent);
      this.openTerritoriesPage();
    });
    this.layer.addLayer(label);
    return label;
  }

  /**
   * Decide what each patch can say about itself, now, at this zoom.
   *
   * The count chip goes first, and it is dropped for one reason: it is bigger
   * than the patch it belongs to. Zoomed out far enough, a fixed-size badge
   * stops marking a patch and starts covering it - a dozen of them become a
   * row of labels over the borders they were drawn to sit inside, which is the
   * one thing on this map that has to stay readable. Measured across its
   * width, so a chip goes at a wider zoom than the circle before it did: it is
   * the wider shape, and it is the width that runs out of patch first. A
   * dropped chip is also no longer in the names' way, which is why it cannot
   * wait until after the obstacles have been collected.
   *
   * Then the names. Bigger patches are placed first and each name tries to
   * avoid markers, controls, and names already placed. A name may extend past
   * the largest circle that fits in its polygon — long narrow territories are
   * not circles, and treating them as one hid labels that plainly had room.
   * Its centre stays inside the safe clearance around its own anchor, which
   * keeps the colour swatch attached to the territory it identifies.
   *
   * If every valid position touches another map object, use the position with
   * the least overlap rather than deleting the territory's identity. Only a
   * label whose anchor cannot be placed wholly inside the viewport is hidden.
   */
  private layoutLabels(): void {
    const map = this.map;
    if (!map) return;

    for (const mark of this.marks) {
      mark.count?.getElement()?.classList
        .toggle('tr-count-hidden', room(mark.anchor, map) < mark.radius);
    }

    const taken: DOMRect[] = [
      ...controlRects(map),
      // Everything else the map is already saying. A lead cluster's count is
      // the reason somebody is looking at this patch; a name written over it
      // trades one fact for another rather than adding one.
      ...markerRects(map),
      ...(this.obstacles?.() ?? []).map(el => el.getBoundingClientRect()),
    ].filter(box => box.width > 0 && box.height > 0);
    const ordered = [...this.marks].sort((a, b) =>
      room(b.anchor, map) - room(a.anchor, map));

    const view = map.getContainer().getBoundingClientRect();

    // THE CHIPS FIRST, and they move now. A lead pin and a territory's lead
    // count are two different answers to the same question sitting on top of
    // each other, and the badge was the one nailed down: only names looked
    // for a clear spot, so on a busy patch the numbers landed on the very
    // markers they are counting.
    //
    // It stays inside the clearance around its own anchor, for the reason
    // names do: a chip that wandered towards a border would stop saying
    // plainly which patch it belongs to, and being readable somewhere else is
    // not the same as being right.
    const shifted = new Map<PatchMarks, boolean>();
    for (const mark of ordered) {
      const chip = mark.count?.getElement();
      if (!chip || chip.classList.contains('tr-count-hidden')) continue;

      const at = map.latLngToContainerPoint(mark.anchor.at);
      const space = room(mark.anchor, map);
      // MEASURED AS IF IT WERE STILL AT THE ANCHOR. Every offset below is
      // applied relative to the anchor, so the boxes they are chosen from
      // have to start there too. Measuring where the chip currently sits made
      // the two disagree by exactly the last offset, and the badge oscillated
      // under the pointer: a chip that had moved found its own position
      // clear, was told to sit at the anchor, overlapped again, moved back,
      // and did that for as long as anything kept the layout running.
      const shownAt = map.latLngToContainerPoint(mark.count!.getLatLng());
      const home = at.subtract(shownAt);
      const origin = translatedRect(chip.getBoundingClientRect(), home.x, home.y);
      let best: { dx: number; dy: number; box: DOMRect; overlap: number } | null = null;

      for (const [dx, dy] of placements(0, origin, space, 0)) {
        if (Math.hypot(dx, dy) > space) continue;
        const box = translatedRect(origin, dx, dy);
        if (!contains(view, box)) continue;
        const overlap = taken.reduce(
          (total, other) => total + overlapArea(box, other), 0);
        if (overlap === 0) { best = { dx, dy, box, overlap }; break; }
        if (!best || overlap < best.overlap) best = { dx, dy, box, overlap };
      }
      if (!best) continue;

      if (best.dx !== 0 || best.dy !== 0) {
        mark.count!.setLatLng(
          map.containerPointToLatLng(at.add(L.point(best.dx, best.dy))));
        shifted.set(mark, true);
      } else if (!mark.count!.getLatLng().equals(mark.anchor.at)) {
        // Back where it belongs. Always measured from the anchor rather than
        // from wherever the last pass left it, or the badge would creep a
        // step further out on every pan.
        mark.count!.setLatLng(mark.anchor.at);
      }
      // The box we CHOSE, not one read back from the DOM. Leaflet has not
      // necessarily repositioned the element by the time this line runs, so
      // measuring it again would sometimes describe where the chip was a
      // moment ago - and the name would then be placed around a ghost.
      taken.push(best.box);
    }

    for (const mark of ordered) {
      const label = mark.label;
      const el = label.getElement();
      if (!el) continue;
      el.classList.remove('tr-label-hidden');
      const space = room(mark.anchor, map);
      el.style.setProperty(
        '--tr-label-font-size',
        `${territoryLabelFontPx(space)}px`,
      );
      // Nothing to stand off, once the chip has been dropped.
      const dropped = mark.count?.getElement()
        ?.classList.contains('tr-count-hidden') ?? true;
      // Nothing to stand off once the chip has been dropped - or once it has
      // moved off the anchor, which is now a place the name can have. The
      // chip's real box is in `taken` either way, so this only decides where
      // the name STARTS looking.
      const standOff = !dropped && !shifted.get(mark);
      const clear = standOff ? mark.halfHeight : 0;
      const clearBeside = standOff ? mark.radius : 0;

      // Just clear of the chip first, then a few steps further off it. A name that would land on something can usually sit a little
      // above or below and still be plainly on its own patch - which is what
      // "do not UNNECESSARILY overlap" asks for. Hiding is the last resort,
      // not the first: the map dropped nine names out of twelve when a
      // collision meant give up, and a map that will not say what its patches
      // are called has solved the wrong problem.
      setOffset(label, 0, 0);
      const origin = el.getBoundingClientRect();
      let placed: { readonly dx: number; readonly dy: number; readonly box: DOMRect } | null = null;
      let fallback: {
        readonly dx: number;
        readonly dy: number;
        readonly overlap: number;
      } | null = null;
      for (const [dx, dy] of placements(clear, origin, space, clearBeside)) {
        const box = translatedRect(origin, dx, dy);
        // The anchor's clearance is a circle known to be inside the polygon.
        // Keep the LABEL CENTRE in it; requiring every corner of a wide label
        // to fit in that circle mistakes a long territory for a small one.
        const centreInsideShape = Math.hypot(dx, dy) <= space;
        if (!centreInsideShape || !contains(view, box)) continue;
        const overlap = taken.reduce(
          (total, other) => total + overlapArea(box, other), 0);
        if (overlap === 0) {
          placed = { dx, dy, box };
          break;
        }
        if (!fallback || overlap < fallback.overlap) {
          fallback = { dx, dy, overlap };
        }
      }

      if (!placed && fallback) {
        placed = {
          dx: fallback.dx,
          dy: fallback.dy,
          box: translatedRect(origin, fallback.dx, fallback.dy),
        };
      }
      if (placed) {
        setOffset(label, placed.dx, placed.dy);
        taken.push(placed.box);
        continue;
      }
      setOffset(label, 0, 0);
      el.classList.add('tr-label-hidden');
    }
  }

  override clear(): void {
    this.endEdit();
    this.layer.clearLayers();
    this.marks.length = 0;
    this.shapesByObjectId.clear();
  }
}

function translatedRect(rect: DOMRect, dx: number, dy: number): DOMRect {
  return new DOMRect(
    rect.x + dx,
    rect.y + dy,
    rect.width,
    rect.height,
  );
}

/**
 * Territory name size for the room its polygon has at the current zoom.
 * Discrete steps follow the cluster badge rule: stable readable sizes rather
 * than a continuously swelling label, bounded so neither a sliver becomes
 * illegible nor a large territory takes over the map.
 */
export function territoryLabelFontPx(space: number): number {
  if (space < 45) return 11;
  if (space < 90) return 13;
  if (space < 160) return 15;
  return 17;
}

/**
 * Where a name may sit, in pixels from its anchor, best first.
 *
 * Dead centre used to head this list and is now the one place the name cannot
 * go: the count chip stands there. So it starts immediately above the chip,
 * tries immediately below, then steps outwards, then either side.
 *
 * Pixels rather than fractions of the patch's room, because what the name has
 * to clear is a badge of a fixed size. A fraction gets it wrong in both
 * directions - on a roomy patch half the clearance is a hundred pixels, which
 * throws the name onto the boundary it was placed to avoid; on a tight one it
 * is four, which does not clear the badge at all. The step outwards is still
 * a fraction, because THAT one is about the patch: it is how far there is to
 * go before running out of shape.
 */
export function placements(
  clear: number,
  size: DOMRect,
  space: number,
  clearBeside: number = clear,
): readonly (readonly [number, number])[] {
  const gap = 2;
  const above = clear + size.height / 2 + gap;
  // Sideways is its own clearance because the badge is a chip: what a name
  // has to get past to sit beside it is its width, and passing the height
  // twice would drop the name straight onto the numbers.
  const beside = clearBeside + size.width / 2 + gap;
  const step = Math.max(space * 0.25, 8);
  const around: readonly (readonly [number, number])[] = [
    [0, -above], [0, above],
    [0, -(above + step)], [0, above + step],
    [-beside, 0], [beside, 0],
    [-beside, -above], [beside, -above],
    [-beside, above], [beside, above],
    [-(beside + step), 0], [beside + step, 0],
  ];
  // With no chip, the anchor is the clearest statement of ownership. With a
  // chip, centre is the last-resort candidate rather than a reason to erase
  // the name when every surrounding position is occupied.
  const centre = [0, 0] as const;
  return clear === 0 && clearBeside === 0
    ? [centre, ...around]
    : [...around, centre];
}

function setOffset(label: L.Tooltip, dx: number, dy: number): void {
  label.options.offset = L.point(Math.round(dx), Math.round(dy));
  label.update();
}

/**
 * The chip one patch shows: what it holds, in the patch's colour.
 *
 * Leads first. It is a leads page, and the patch was drawn to hold a pipeline;
 * the customers in it are the standing business behind that.
 *
 * A withheld customer count leaves the chip one cell wide rather than putting
 * a nought in it. The two are not the same thing to a reader - one says the
 * patch is empty of customers, the other says nobody told you - and drawing a
 * zero for the second is the map telling a lie on the server's behalf.
 */
function chipOf(row: TerritoryWithMetrics, color: string): CountChip {
  return {
    color,
    className: 'tr-count',
    parts: [
      { count: row.leads, color: MapFeatureColors.leads },
      ...(row.customers == null
        ? []
        : [{ count: row.customers, color: MapFeatureColors.customers }]),
    ],
  };
}

/** What one patch put on the map besides its outline, and where. Kept as a
 *  set because the two are laid out against each other: the name stands off
 *  the chip, and stops having to once the chip has been dropped. */
interface PatchMarks {
  readonly label: L.Tooltip;
  readonly count?: L.Marker;
  readonly anchor: LabelAnchor;
  /** Half the chip's width, in pixels - the room the name does not have, and
   *  the reach that decides whether the chip fits inside the patch at all. */
  readonly radius: number;
  /** Half its height. What a name sitting above or below has to clear, which
   *  on a chip is a much shorter distance than clearing it sideways. */
  readonly halfHeight: number;
}

/** How many pixels of empty shape an anchor has around it, at the current
 *  zoom. Degrees are meaningless to something measured in px. */
function room(anchor: LabelAnchor, map: L.Map): number {
  const at = map.latLngToContainerPoint(anchor.at);
  const edge = map.latLngToContainerPoint(
    L.latLng(anchor.at.lat + anchor.clearance, anchor.at.lng));
  return Math.abs(at.y - edge.y);
}

/** The map's own furniture - zoom buttons, attribution, anything else Leaflet
 *  puts in a corner. A name written under the zoom control is not readable. */
function controlRects(map: L.Map): DOMRect[] {
  const container = map.getContainer()
    .querySelector('.leaflet-control-container');
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>('.leaflet-control'))
    .map(el => el.getBoundingClientRect())
    .filter(box => box.width > 0 && box.height > 0);
}

/** Every marker the map is already showing: lead clusters, place pins. */
function markerRects(map: L.Map): DOMRect[] {
  return Array.from(
    map.getContainer().querySelectorAll<HTMLElement>('.leaflet-marker-icon'))
    // Not our own count chips. They are markers like any other, so a chip
    // looking for a clear spot would find itself in the way and step off a
    // patch to avoid a badge that was about to move anyway. Each one is put
    // into the obstacle list by hand once it has been placed.
    .filter(el => !el.classList.contains('map-count-chip-icon'))
    .map(el => el.getBoundingClientRect());
}

/** A label half off the edge is a label nobody can read the end of. */
function contains(outer: DOMRect, inner: DOMRect): boolean {
  return inner.left >= outer.left && inner.right <= outer.right
    && inner.top >= outer.top && inner.bottom <= outer.bottom;
}

function overlaps(a: DOMRect, b: DOMRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function overlapArea(a: DOMRect, b: DOMRect): number {
  if (!overlaps(a, b)) return 0;
  return (Math.min(a.right, b.right) - Math.max(a.left, b.left))
    * (Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
}

/** The rings Leaflet parsed out of the boundary, so the geometry is read once. */
function ringsOfLayer(shape: L.GeoJSON): L.LatLng[][] {
  const rings: L.LatLng[][] = [];
  shape.eachLayer(child => {
    const latlngs = (child as L.Polygon).getLatLngs?.();
    if (!Array.isArray(latlngs)) return;
    for (const part of latlngs as unknown[]) {
      if (Array.isArray(part) && part.length && part[0] instanceof L.LatLng) {
        rings.push(part as L.LatLng[]);
      }
    }
  });
  return rings;
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** Bounds of a GeoJSON boundary, or null when it will not parse. */
export function boundsOf(boundaryGeoJson: string): L.LatLngBounds | null {
  try {
    const bounds = L.geoJSON(JSON.parse(boundaryGeoJson)).getBounds();
    return bounds.isValid() ? bounds : null;
  } catch {
    return null;
  }
}
